import * as THREE from 'three'
import * as dat from 'dat.gui'

import Sizes from './Utils/Sizes.js'
import Time from './Utils/Time.js'
import World from './World/index.js'
import Resources from './Resources.js'
import Camera from './Camera.js'
import site from '../content/site.js'
import Menu from './Menu.js'
import SoundToggles from './SoundToggles.js'
import { isMobileLayout, onMobileLayoutChange } from './Utils/layout.js'
import { themes } from '../content/palette.js'

import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import BlurPass from './Passes/Blur.js'
import GlowsPass from './Passes/Glows.js'

export default class Application
{
    /**
     * Constructor
     */
    constructor(_options)
    {
        // Options
        this.$canvas = _options.$canvas

        // Set up
        this.time = new Time()
        this.sizes = new Sizes()
        this.resources = new Resources()

        this.setConfig()
        this.setDebug()
        this.setRenderer()
        this.setCamera()
        this.setPasses()
        this.setWorld()

        // Telefon düzeni (dokunmatik ya da ≤ 760 px pencere): ekran kumandaları görünür; pencere genişleyince
        // (dokunmatik olmayan cihazda) gizlenir
        if(isMobileLayout())
        {
            this.showTouchControls()
        }
        onMobileLayoutChange((_mobile) =>
        {
            if(_mobile)
            {
                this.showTouchControls()
            }
            else if(this.world.controls.touch && !this.config.touch)
            {
                this.world.controls.touch.setVisible(false)
            }
        })
        this.setTitle()
        this.menu = new Menu({ config: this.config, world: this.world })
        this.soundToggles = new SoundToggles({ world: this.world, config: this.config })
        this.setBrandLogo()
    }

    /**
     * Set config
     */
    setConfig()
    {
        this.config = {}
        this.config.debug = window.location.hash === '#debug'

        // Garajda seçilen araç bu tarayıcıda hatırlanır (yalnız kolaylık; okunamazsa varsayılan araç)
        this.config.vehicle = null
        try
        {
            this.config.vehicle = window.localStorage.getItem('llpause.vehicle')
        }
        catch(_error) {}

        // Renk teması (T tuşu / TEMA alanı) — aynı şekilde hatırlanır
        this.config.theme = 'lavanta'
        try
        {
            const theme = window.localStorage.getItem('llpause.theme')
            if(themes[theme])
            {
                this.config.theme = theme
            }
        }
        catch(_error) {}
        // Dokunmatik kumandalar (joystick, gaz, fren). START kalktı: açılış ilk dokunuştan önce biter, kumandalar
        // yalnız dokununca kurulursa hiç görünmüyordu → birincil girişi dokunma olan cihazda dünya kurulur kurulmaz
        // (constructor) kurulur, açılış onları gösterir. Sonradan dokunulursa (dokunmatik dizüstü) kurulup gösterilir.
        this.config.touch = window.matchMedia('(pointer: coarse)').matches

        // Ekran kumandalarını kur (bir kez) ve göster; açılış bittiyse hemen görünür yap
        this.showTouchControls = () =>
        {
            if(!this.world.controls.touch)
            {
                this.world.controls.setTouch()
            }
            this.world.controls.touch.setVisible(true)

            if(this.world.startingScreen && this.world.startingScreen.begun)
            {
                window.setTimeout(() => this.world.controls.touch.reveal(), 100)
            }
        }
        this.enableTouch = () =>
        {
            this.config.touch = true
            this.showTouchControls()
        }

        window.addEventListener('touchstart', () =>
        {
            this.enableTouch()

            this.passes.horizontalBlurPass.strength = 1
            this.passes.horizontalBlurPass.material.uniforms.uStrength.value = new THREE.Vector2(this.passes.horizontalBlurPass.strength, 0)
            this.passes.verticalBlurPass.strength = 1
            this.passes.verticalBlurPass.material.uniforms.uStrength.value = new THREE.Vector2(0, this.passes.verticalBlurPass.strength)
        }, { once: true })
    }

    /**
     * Set debug
     */
    setDebug()
    {
        if(this.config.debug)
        {
            this.debug = new dat.GUI({ width: 420 })
        }
    }

    /**
     * Set renderer
     */
    setRenderer()
    {
        // Scene
        this.scene = new THREE.Scene()

        // Renderer
        this.renderer = new THREE.WebGLRenderer({
            canvas: this.$canvas,
            alpha: true,
            powerPreference: 'high-performance'
        })
        // this.renderer.setClearColor(0x414141, 1)
        this.renderer.setClearColor(0x000000, 1)
        // Kenar yumuşatma kapalı: masaüstünde en az 1.5× çizilir (1× ekranda yumuşatma yerine geçer), en çok 2×
        // (Retina değişmez). Dokunmatikte en çok 1.5×: 3× telefonda bile piksel ~%44 azalır, fark gözle seçilmez.
        // EffectComposer piksel oranını kurulurken alır → setPasses'tan önce
        this.renderer.setPixelRatio(this.config.touch ? Math.min(window.devicePixelRatio, 1.5) : Math.min(Math.max(window.devicePixelRatio, 1.5), 2))
        this.renderer.setSize(this.sizes.viewport.width, this.sizes.viewport.height)
        this.renderer.autoClear = false

        // Resize event
        this.sizes.on('resize', () =>
        {
            this.renderer.setSize(this.sizes.viewport.width, this.sizes.viewport.height)
        })
    }

    /**
     * Set camera
     */
    setCamera()
    {
        this.camera = new Camera({
            time: this.time,
            sizes: this.sizes,
            renderer: this.renderer,
            debug: this.debug,
            config: this.config
        })

        this.scene.add(this.camera.container)

        this.time.on('tick', () =>
        {
            if(this.world && this.world.car)
            {
                this.camera.target.x = this.world.car.chassis.object.position.x
                this.camera.target.y = this.world.car.chassis.object.position.y
            }
        })
    }

    setPasses()
    {
        this.passes = {}

        // Debug
        if(this.debug)
        {
            this.passes.debugFolder = this.debug.addFolder('postprocess')
            // this.passes.debugFolder.open()
        }

        this.passes.composer = new EffectComposer(this.renderer)

        // Create passes
        this.passes.renderPass = new RenderPass(this.scene, this.camera.instance)

        this.passes.horizontalBlurPass = new ShaderPass(BlurPass)
        this.passes.horizontalBlurPass.strength = this.config.touch ? 0 : 1
        this.passes.horizontalBlurPass.material.uniforms.uResolution.value = new THREE.Vector2(this.sizes.viewport.width, this.sizes.viewport.height)
        this.passes.horizontalBlurPass.material.uniforms.uStrength.value = new THREE.Vector2(this.passes.horizontalBlurPass.strength, 0)

        this.passes.verticalBlurPass = new ShaderPass(BlurPass)
        this.passes.verticalBlurPass.strength = this.config.touch ? 0 : 1
        this.passes.verticalBlurPass.material.uniforms.uResolution.value = new THREE.Vector2(this.sizes.viewport.width, this.sizes.viewport.height)
        this.passes.verticalBlurPass.material.uniforms.uStrength.value = new THREE.Vector2(0, this.passes.verticalBlurPass.strength)

        // Debug
        if(this.debug)
        {
            const folder = this.passes.debugFolder.addFolder('blur')
            folder.open()

            folder.add(this.passes.horizontalBlurPass.material.uniforms.uStrength.value, 'x').step(0.001).min(0).max(10)
            folder.add(this.passes.verticalBlurPass.material.uniforms.uStrength.value, 'y').step(0.001).min(0).max(10)
        }

        this.passes.glowsPass = new ShaderPass(GlowsPass)
        this.passes.glowsPass.color = themes[this.config.theme].glow
        this.passes.glowsPass.material.uniforms.uPosition.value = new THREE.Vector2(0, 0.25)
        this.passes.glowsPass.material.uniforms.uRadius.value = 0.7
        this.passes.glowsPass.material.uniforms.uColor.value = new THREE.Color(this.passes.glowsPass.color)
        this.passes.glowsPass.material.uniforms.uColor.value.convertLinearToSRGB()
        this.passes.glowsPass.material.uniforms.uAlpha.value = 0.4 // 0.55 renkleri soldururdu

        // Debug
        if(this.debug)
        {
            const folder = this.passes.debugFolder.addFolder('glows')
            folder.open()

            folder.add(this.passes.glowsPass.material.uniforms.uPosition.value, 'x').step(0.001).min(- 1).max(2).name('positionX')
            folder.add(this.passes.glowsPass.material.uniforms.uPosition.value, 'y').step(0.001).min(- 1).max(2).name('positionY')
            folder.add(this.passes.glowsPass.material.uniforms.uRadius, 'value').step(0.001).min(0).max(2).name('radius')
            folder.addColor(this.passes.glowsPass, 'color').name('color').onChange(() =>
            {
                this.passes.glowsPass.material.uniforms.uColor.value = new THREE.Color(this.passes.glowsPass.color)
            })
            folder.add(this.passes.glowsPass.material.uniforms.uAlpha, 'value').step(0.001).min(0).max(1).name('alpha')
        }

        // Add passes
        this.passes.composer.addPass(this.passes.renderPass)
        this.passes.composer.addPass(this.passes.horizontalBlurPass)
        this.passes.composer.addPass(this.passes.verticalBlurPass)
        this.passes.composer.addPass(this.passes.glowsPass)

        // Time tick
        this.time.on('tick', () =>
        {
            this.passes.horizontalBlurPass.enabled = this.passes.horizontalBlurPass.material.uniforms.uStrength.value.x > 0
            this.passes.verticalBlurPass.enabled = this.passes.verticalBlurPass.material.uniforms.uStrength.value.y > 0

            // Renderer
            this.passes.composer.render()
            // this.renderer.domElement.style.background = 'black'
            // this.renderer.render(this.scene, this.camera.instance)
        })

        // Resize event
        this.sizes.on('resize', () =>
        {
            this.renderer.setSize(this.sizes.viewport.width, this.sizes.viewport.height)
            this.passes.composer.setSize(this.sizes.viewport.width, this.sizes.viewport.height)
            this.passes.horizontalBlurPass.material.uniforms.uResolution.value.x = this.sizes.viewport.width
            this.passes.horizontalBlurPass.material.uniforms.uResolution.value.y = this.sizes.viewport.height
            this.passes.verticalBlurPass.material.uniforms.uResolution.value.x = this.sizes.viewport.width
            this.passes.verticalBlurPass.material.uniforms.uResolution.value.y = this.sizes.viewport.height
        })
    }

    /**
     * Set world
     */
    setWorld()
    {
        this.world = new World({
            config: this.config,
            debug: this.debug,
            resources: this.resources,
            time: this.time,
            sizes: this.sizes,
            camera: this.camera,
            scene: this.scene,
            renderer: this.renderer,
            passes: this.passes
        })
        this.scene.add(this.world.container)
    }

    /**
     * Set title
     */
    setTitle()
    {
        document.title = site.title
    }

    /**
     * Destructor
     */
    destructor()
    {
        this.time.off('tick')
        this.sizes.off('resize')

        this.camera.orbitControls.dispose()
        this.renderer.dispose()
        this.debug.destroy()
    }

    // Ekranın üst ortasında beyaz TAIGA logosu (static/ui/taiga-logo-white.svg; tıklamayı engellemez)
    setBrandLogo()
    {
        this.brandLogo = document.createElement('img')
        this.brandLogo.className = 'brand-logo'
        this.brandLogo.src = './ui/taiga-logo-white.svg'
        this.brandLogo.alt = 'TAIGA'
        this.brandLogo.width = 3869
        this.brandLogo.height = 869
        document.body.append(this.brandLogo)
    }
}
